import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';

const AssignPackageMilestoneModal = ({ isOpen, onClose, dentistId, caseId, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [dentistProfile, setDentistProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && dentistId) {
      fetchDentistAndPackages();
    }
  }, [isOpen, dentistId]);

const fetchDentistAndPackages = async () => {
  try {
    setLoading(true);

    // 1. Get the dentist's profile (country_id only)
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('id, full_name, email, country_id, countries ( id, country_name, currency_code )')
      .eq('id', dentistId)
      .single();

    if (profileError) throw profileError;
    if (!profile) throw new Error('Profil du dentiste introuvable');

    setDentistProfile(profile);

    if (!profile.country_id) {
      toast.error("Le dentiste n'a pas de pays assigné");
      setPackages([]);
      return;
    }

    // 2. Get the case's current package
    const { data: caseRow, error: caseError } = await supabase
      .from('cases')
      .select('package')
      .eq('id', caseId)
      .single();

    if (!caseError && caseRow?.package) {
      setCurrentPackageId(caseRow.package);
      setSelectedPackageId(caseRow.package);
    }

    // 3. Fetch active packages for that country
    const { data: pkgs, error: pkgError } = await supabase
      .from('commercial_packages')
      .select('*, countries ( id, currency_code )')
      .eq('country_id', profile.country_id)
      .eq('status', 'active')
      .order('tier', { ascending: true })
      .order('base_price', { ascending: true });

    if (pkgError) throw pkgError;
    setPackages(pkgs || []);
  } catch (err) {
    console.error('Error fetching dentist packages:', err);
    toast.error('Erreur lors du chargement des packages: ' + err.message);
  } finally {
    setLoading(false);
  }
};
const handleSave = () => {
  if (!selectedPackageId) {
    toast.error('Veuillez sélectionner un package');
    return;
  }
  onSuccess?.(selectedPackageId); // parent handles the DB write
  onClose();
};

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(price);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':   return 'bg-amber-700 text-white';
      case 'silver':   return 'bg-gray-400 text-gray-900';
      case 'gold':     return 'bg-yellow-500 text-gray-900';
      case 'platinum': return 'bg-purple-600 text-white';
      default:         return 'bg-gray-500 text-white';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Package" size={20} className="text-primary" />
              {currentPackageId ? 'Modifier le package' : 'Assigner un package'}
            </h2>
            {dentistProfile && (
              <p className="text-sm text-muted-foreground mt-1">
                {dentistProfile.full_name} — {dentistProfile.countries?.country_name || 'Pays inconnu'}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={24} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement des packages...</span>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="PackageX" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Aucun package disponible pour le pays de ce dentiste.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {packages.map((pkg) => {
                const isSelected  = selectedPackageId === pkg.id;
                const isCurrent   = currentPackageId === pkg.id;
                const currencyCode = pkg?.countries?.currency_code || 'EUR';

                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-150 ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40 hover:bg-muted/30'
                    }`}
                  >
                    {/* Current badge */}
                    {isCurrent && (
                      <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-success text-success-foreground text-[10px] font-bold uppercase rounded-full">
                        Actuel
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {/* Left: radio + info */}
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-primary' : 'border-muted-foreground'
                          }`}
                        >
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">{pkg.package_name}</span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${getTierColor(pkg.tier)}`}
                            >
                              {pkg.tier}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{pkg.package_code}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {pkg.included_units} unités incluses
                          </p>
                        </div>
                      </div>

                      {/* Right: price */}
                      <div className="text-right flex-shrink-0 ml-3">
                        {pkg.discount_percentage > 0 && (
                          <p className="text-xs text-muted-foreground line-through">
                            {formatPrice(pkg.base_price, currencyCode)}
                          </p>
                        )}
                        <p className="text-lg font-bold text-foreground">
                          {formatPrice(pkg.final_price, currencyCode)}
                        </p>
                        {pkg.discount_percentage > 0 && (
                          <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">
                            -{pkg.discount_percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedPackageId || loading || saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                En cours...
              </>
            ) : (
              <>
                <Icon name="Check" size={16} />
                Valider
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPackageMilestoneModal;